<template>
  <component
    v-if="currentStepComponent"
    v-bind="componentProps"
    :is="currentStepComponent"
    :key="`${flowType}-${currentStepId}`"
    :data="data"
    :errors="errors"
    :endpoint="currentStepEndpoint"
    @update="handleUpdate"
    @next="handleNext"
    @go-to-step="handleGoToStep"
    @reset-package="handleResetPackage"
    @start="handleStart"
    @increase-product="handleIncreaseProduct"
    @decrease-product="handleDecreaseProduct"
    @add-to-cart="handleAddToCart"
    @remove-product="handleRemoveProduct"
    @remove-food="handleRemoveFoodDrink"
    @remove-packaging="handleRemovePackaging"
    @select-base="handleSelectBase"
    @printing-updated="handlePrintingUpdated"
    @remove-base-package="handleRemoveBasePackage"
  />
  <div v-else class="alert alert-danger">
    <strong>Fout:</strong> De stapcomponent voor deze stap is niet gevonden.
    Controleer de 'component' property in flowConfig.js en de registratie in
    Steps.vue.
  </div>
</template>

<script>
import { flowConfig } from './config/flowConfig.js';

import BaseSelection from './steps/BaseSelection.vue';
import Products from './steps/Products.vue';
import FoodAndDrinks from './steps/FoodAndDrinks.vue';
import Packaging from './steps/Packaging.vue';
import Personalization from './steps/Personalization.vue';

const componentRegistry = {
  BaseSelection,
  Products,
  FoodAndDrinks,
  Packaging,
  Personalization,
};

export default {
  name: 'Steps',
  props: {
    flowType: {
      type: String,
      required: true,
      validator: (value) => ['compose', 'selectPackage'].includes(value),
    },
    currentStepId: {
      type: String,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: [
    'update',
    'next',
    'go-to-step',
    'reset-package',
    'select-base',
    'printing-updated',
  ],
  computed: {
    currentFlowConfig() {
      return flowConfig[this.flowType] || { steps: [] };
    },

    currentStepConfig() {
      return (
        this.currentFlowConfig.steps.find(
          (step) => step.id === this.currentStepId,
        ) || {}
      );
    },

    currentStepComponent() {
      const componentName = this.currentStepConfig.component;
      if (!componentName) {
        return null;
      }
      if (!componentRegistry[componentName]) {
        console.error(
          `Component '${componentName}' for stepId: ${this.currentStepId} in flowType: ${this.flowType} is not registered in Steps.vue.`,
        );
        return null;
      }
      return componentRegistry[componentName];
    },

    currentStepEndpoint() {
      return this.currentStepConfig.endpoint || '';
    },

    currentStepIndex() {
      return this.currentFlowConfig.steps.findIndex(
        (step) => step.id === this.currentStepId,
      );
    },

    componentProps() {
      const baseProps = {
        flowType: this.flowType,
        currentStep: this.currentStepIndex >= 0 ? this.currentStepIndex : 0,
      };

      if (this.flowType === 'selectPackage') {
        baseProps.mode = 'base';
        const packageType = this.data.packageType || 'large';
        baseProps.packageType = packageType;
        baseProps.isMailboxSession = this.data.isMailboxSession || false;
      }

      if (this.flowType === 'compose') {
        baseProps.mode = 'custom';
        const packageType = this.data.packageType || 'large';
        baseProps.packageType = packageType;
        baseProps.isMailboxSession = this.data.isMailboxSession || false;
      }

      return baseProps;
    },
  },
  watch: {
    'data.isMailboxSession': {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$forceUpdate();
        }
      },
      immediate: false,
    },
    'data.packageType': {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$forceUpdate();
        }
      },
      immediate: false,
    },
  },
  methods: {
    reloadData() {
      this.$forceUpdate();
    },
    handleUpdate(payload) {
      this.$emit('update', payload);
    },

    handleNext() {
      this.$emit('next');
    },

    handleGoToStep(stepId) {
      this.$emit('go-to-step', stepId);
    },

    handleResetPackage() {
      this.$emit('reset-package');
    },

    handleStart(startData) {
      this.$emit('start', startData);
    },

    handleIncreaseProduct(productId) {
      this.$emit('increase-product', productId);
    },

    handleDecreaseProduct(productId) {
      this.$emit('decrease-product', productId);
    },

    handleAddToCart() {
      this.$emit('add-to-cart');
    },

    handleRemoveProduct(productId) {
      this.$emit('remove-product', productId);
    },

    handleRemoveFoodDrink(foodId) {
      this.$emit('remove-food', foodId);
    },

    handleRemovePackaging() {
      this.$emit('remove-packaging');
    },

    handleSelectBase(pakket) {
      this.$emit('select-base', pakket);
    },

    handlePrintingUpdated(printingData) {
      this.$emit('printing-updated', printingData);
    },

    handleRemoveBasePackage() {
      this.$emit('remove-base-package');
    },
  },
};
</script>
